import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";

/* One renderer for every Knowledge Base surface (KB page, "?" help drawer, portal help, editor preview, AI answers).
   GitHub-flavoured markdown + three conventions of our own:
     ![caption](/kb-shots/name.png)          → framed screenshot with caption, click to enlarge
     > **Tip:** / **Note:** / **Warning:**   → coloured callout
     [Settings → Integrations](/settings?tab=integrations) → in-app link (router), external links open in a new tab */

const CALLOUTS = {
  tip: "border-emerald-500/60 bg-emerald-500/10",
  note: "border-brand/60 bg-brand/10",
  warning: "border-amber-500/70 bg-amber-500/10",
  important: "border-red-500/70 bg-red-500/10",
};

function calloutKind(children) {
  const first = Array.isArray(children) ? children[0] : children;
  const strong = first?.props?.children;
  const text = Array.isArray(strong) ? strong[0] : strong;
  const inner = typeof text === "string" ? text : (text?.props?.children ?? "");
  const label = (Array.isArray(inner) ? inner[0] : inner || "").toString().replace(":", "").trim().toLowerCase();
  return CALLOUTS[label] ? label : null;
}

function Figure({ src, alt, compact }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <figure className={`my-3 ${compact ? "" : "sm:my-4"}`} data-testid="kb-figure">
        <button type="button" onClick={() => setOpen(true)} className="block w-full rounded-lg border border-border bg-background overflow-hidden shadow-sm hover:shadow-md transition-shadow" aria-label={`Enlarge: ${alt || "screenshot"}`}>
          <img src={src} alt={alt || ""} loading="lazy" className="w-full h-auto block" onError={(e) => { e.currentTarget.closest("figure").style.display = "none"; }} />
        </button>
        {alt && <figcaption className="text-[11px] text-muted-foreground mt-1.5 text-center">{alt}</figcaption>}
      </figure>
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="kb-figure-lightbox">
          <button type="button" className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setOpen(false)} aria-label="Close"><X size={22} /></button>
          <img src={src} alt={alt || ""} className="max-w-full max-h-full rounded-md shadow-2xl" />
        </div>
      )}
    </>
  );
}

const LINK_CLS = "text-primary underline underline-offset-2 hover:opacity-80";

/* `#kb/<slug>` links open another guide in the same surface (modal / drawer) via onOpenArticle;
   without a handler they fall back to the full Knowledge Base page. */
function SmartLink({ href = "", children, onOpenArticle }) {
  if (href.startsWith("#kb/")) {
    const slug = href.slice(4);
    if (onOpenArticle) {
      return <button type="button" onClick={() => onOpenArticle(slug)} className={LINK_CLS} data-testid={`kb-link-${slug}`}>{children}</button>;
    }
    return <Link to={`/knowledge-base?article=${slug}`} className={LINK_CLS} data-testid={`kb-link-${slug}`}>{children}</Link>;
  }
  const internal = href.startsWith("/") && !href.startsWith("//");
  if (internal) return <Link to={href} className={LINK_CLS}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLS}>{children}</a>;
}

export function KbMarkdown({ content, compact = false, onOpenArticle }) {
  if (!content) return null;
  const base = compact ? "text-xs" : "text-sm";
  const components = {
    h1: ({ children }) => <h2 className={`${compact ? "text-sm" : "text-base"} font-semibold mt-5 mb-2 first:mt-0`}>{children}</h2>,
    h2: ({ children }) => <h2 className={`${compact ? "text-sm" : "text-base"} font-semibold mt-5 mb-2 first:mt-0`}>{children}</h2>,
    h3: ({ children }) => <h3 className={`${compact ? "text-xs" : "text-sm"} font-semibold mt-4 mb-1.5`}>{children}</h3>,
    p: ({ node, children }) => {
      const kids = (node?.children || []).filter((c) => !(c.type === "text" && !c.value.trim()));
      if (kids.length === 1 && kids[0].tagName === "img") return <>{children}</>;  // <figure> must not sit inside <p>
      return <p className={`${base} leading-relaxed mb-2.5`}>{children}</p>;
    },
    ul: ({ children }) => <ul className={`${base} list-disc pl-5 mb-3 space-y-1`}>{children}</ul>,
    ol: ({ children, start }) => <ol start={start} className={`${base} list-decimal pl-5 mb-3 space-y-1.5`}>{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    a: (props) => <SmartLink {...props} onOpenArticle={onOpenArticle} />,
    img: ({ src, alt }) => <Figure src={src} alt={alt} compact={compact} />,
    code: ({ inline, children }) => <code className="px-1 py-0.5 rounded bg-muted font-mono text-[0.85em]">{children}</code>,
    pre: ({ children }) => <pre className="bg-muted rounded-md p-3 text-xs overflow-x-auto mb-3">{children}</pre>,
    table: ({ children }) => <div className="overflow-x-auto mb-3"><table className={`${base} w-full border border-border rounded-md`}>{children}</table></div>,
    th: ({ children }) => <th className="text-left font-semibold bg-muted/60 px-2.5 py-1.5 border-b border-border">{children}</th>,
    td: ({ children }) => <td className="px-2.5 py-1.5 border-b border-border align-top">{children}</td>,
    hr: () => <hr className="my-4 border-border" />,
    blockquote: ({ children }) => {
      const kind = calloutKind(children.filter?.((c) => c?.type) ?? children);
      return (
        <blockquote className={`border-l-4 rounded-r-md px-3 py-2 mb-3 ${kind ? CALLOUTS[kind] : "border-border bg-muted/40"} [&>p]:mb-0`} data-testid={kind ? `kb-callout-${kind}` : "kb-quote"}>
          {children}
        </blockquote>
      );
    },
  };
  return (
    <div className="kb-markdown" data-testid="kb-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{content}</ReactMarkdown>
    </div>
  );
}

/* Back-compat for the older call sites that expect a node list. */
export function renderKbContent(content, opts = {}) {
  return <KbMarkdown content={content} compact={opts.compact} onOpenArticle={opts.onOpenArticle} />;
}

/* Small screenshot preview shown on article rows (help drawer, KB page, portal KB). */
export function KbThumb({ src, size = "md" }) {
  if (!src) return null;
  const cls = size === "sm" ? "w-16 h-11" : "w-24 h-16";
  return (
    <span className={`${cls} shrink-0 rounded-md overflow-hidden border border-border bg-muted/40 block`} data-testid="kb-thumb">
      <img src={src} alt="" loading="lazy" className="w-full h-full object-cover object-top" />
    </span>
  );
}
